import React, { useEffect, useMemo, useState } from "react";
import { NeoButton, NeoInput } from "../../../../../components/Common";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ConfirmDialog from "../../../../../components/Common/ConfirmDialog";
import { useTenant } from "../../../../../hooks/useTenant";
import RunningSessionLibraryService from "../../../../../services/running_session_library_service";
import { RUNNING_SESSION_GROUPS, RUNNING_SESSION_LIBRARY } from "../../domain/running/sessionLibrary";
import { RUNNING_LOCATION_TYPES, RUNNING_SESSION_TYPES, RUNNING_TERRAIN_TYPES } from "../../domain/running/constants";

const optionHtml = (items = [], selected = "") =>
    items.map(item => `<option value="${item.value}" ${item.value === selected ? "selected" : ""}>${item.label}</option>`).join("");

const splitCsv = (value = "") =>
    value.split(",").map(item => item.trim()).filter(Boolean);

const buildEditableSession = (session = {}, formValues = {}) => ({
    ...session,
    name: formValues.name || session.name || "Sessão de corrida",
    group: formValues.group || session.group || "club",
    sessionType: formValues.sessionType || session.sessionType || "easy",
    objective: formValues.objective ?? session.objective ?? "",
    level: formValues.level ?? session.level ?? "",
    estimatedDistanceKm: formValues.estimatedDistanceKm ?? session.estimatedDistanceKm ?? "",
    estimatedDurationMin: formValues.estimatedDurationMin ?? session.estimatedDurationMin ?? "",
    locationType: formValues.locationType || session.locationType || "street",
    terrain: formValues.terrain || session.terrain || "flat",
    tags: formValues.tags || session.tags || ["clube"],
    coachNotes: formValues.coachNotes ?? session.coachNotes ?? "",
    clubSession: session.clubSession || null,
    cyclePlan: session.cyclePlan || null,
    attendancePlan: session.attendancePlan || null,
    blocks: session.blocks || [],
});

const showSessionForm = async (session = {}) => Swal.fire({
    title: session.id ? "Editar sessão de corrida" : "Nova sessão de corrida",
    width: 760,
    html: `
        <div class="row g-3 text-start">
            <div class="col-md-7">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome da sessão</label>
                <input id="run-name" class="form-control" placeholder="Ex: 6x400m ritmo 5K" value="${session.name || ""}">
            </div>
            <div class="col-md-5">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Grupo</label>
                <select id="run-group" class="form-control">${optionHtml(RUNNING_SESSION_GROUPS, session.group || "club")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Tipo</label>
                <select id="run-session-type" class="form-control">${optionHtml(RUNNING_SESSION_TYPES, session.sessionType || "easy")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Local</label>
                <select id="run-location" class="form-control">${optionHtml(RUNNING_LOCATION_TYPES, session.locationType || "street")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Terreno</label>
                <select id="run-terrain" class="form-control">${optionHtml(RUNNING_TERRAIN_TYPES, session.terrain || "flat")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Objetivo</label>
                <input id="run-objective" class="form-control" placeholder="5K, 10K, Base" value="${session.objective || ""}">
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Nível</label>
                <input id="run-level" class="form-control" placeholder="Iniciante, todos..." value="${session.level || ""}">
            </div>
            <div class="col-md-2">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Km</label>
                <input id="run-distance" class="form-control" placeholder="8" value="${session.estimatedDistanceKm || ""}">
            </div>
            <div class="col-md-2">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Min</label>
                <input id="run-duration" class="form-control" placeholder="60" value="${session.estimatedDurationMin || ""}">
            </div>
            <div class="col-12">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Tags</label>
                <input id="run-tags" class="form-control" placeholder="pista, 5k, tiros" value="${(session.tags || ["clube"]).join(", ")}">
            </div>
            <div class="col-12">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Observações do professor</label>
                <textarea id="run-notes" class="form-control" rows="2" placeholder="Foco técnico, logística, hidratação, segurança.">${session.coachNotes || ""}</textarea>
            </div>
        </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: "Salvar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#2f9e44",
    cancelButtonColor: "#6c757d",
    preConfirm: () => {
        const name = document.getElementById("run-name").value.trim();
        if (!name) {
            Swal.showValidationMessage("O nome da sessão é obrigatório.");
            return false;
        }

        return {
            name,
            group: document.getElementById("run-group").value,
            sessionType: document.getElementById("run-session-type").value,
            locationType: document.getElementById("run-location").value,
            terrain: document.getElementById("run-terrain").value,
            objective: document.getElementById("run-objective").value.trim(),
            level: document.getElementById("run-level").value.trim(),
            estimatedDistanceKm: document.getElementById("run-distance").value.trim(),
            estimatedDurationMin: document.getElementById("run-duration").value.trim(),
            tags: splitCsv(document.getElementById("run-tags").value),
            coachNotes: document.getElementById("run-notes").value.trim(),
        };
    },
});

const RunningSessionLibraryPicker = ({ refreshKey = 0, onAddSession }) => {
    const { idTenant, user: authUser } = useTenant();
    const [customSessions, setCustomSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [groupFilter, setGroupFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");

    const loadCustomSessions = React.useCallback(async (mounted = true) => {
        if (!idTenant) return;
        setLoading(true);
        try {
            const data = await RunningSessionLibraryService.getAll(idTenant);
            if (mounted) setCustomSessions(data);
        } catch (error) {
            console.error("Erro ao carregar biblioteca de corrida:", error);
        } finally {
            if (mounted) setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        let mounted = true;
        loadCustomSessions(mounted);
        return () => {
            mounted = false;
        };
    }, [loadCustomSessions, refreshKey]);

    const items = useMemo(() => [
        ...customSessions.map(item => ({ ...item.session, id: item.id, source: "tenant" })),
        ...RUNNING_SESSION_LIBRARY.map(item => ({ ...item, source: "system" })),
    ], [customSessions]);

    const visibleItems = items.filter(item => {
        const query = searchQuery.trim().toLowerCase();
        const matchesGroup = groupFilter === "all" || item.group === groupFilter;
        const matchesSearch = !query ||
            item.name.toLowerCase().includes(query) ||
            (item.objective || "").toLowerCase().includes(query) ||
            (item.tags || []).some(tag => tag.toLowerCase().includes(query));
        return matchesGroup && matchesSearch;
    });

    const handleCreate = async () => {
        const { value: formValues } = await showSessionForm();
        if (!formValues) return;

        try {
            await RunningSessionLibraryService.save({
                idTenant,
                authUser,
                session: buildEditableSession({}, formValues),
            });
            await loadCustomSessions(true);
            toast.success("Sessão criada na biblioteca de corrida.");
        } catch (error) {
            toast.error(error.message || "Erro ao criar sessão.");
        }
    };

    const handleEdit = async (event, item) => {
        event.stopPropagation();
        const { value: formValues } = await showSessionForm(item);
        if (!formValues) return;

        try {
            await RunningSessionLibraryService.update({
                idTenant,
                sessionId: item.id,
                session: buildEditableSession(item, formValues),
            });
            await loadCustomSessions(true);
            toast.success("Sessão atualizada.");
        } catch (error) {
            toast.error(error.message || "Erro ao atualizar sessão.");
        }
    };

    const handleDelete = async (event, item) => {
        event.stopPropagation();
        const confirmed = await ConfirmDialog({
            title: "Excluir sessão?",
            text: `Deseja remover "${item.name}" da biblioteca de corrida?`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#dc3545",
        });

        if (!confirmed) return;

        try {
            await RunningSessionLibraryService.delete({ idTenant, sessionId: item.id });
            await loadCustomSessions(true);
            toast.success("Sessão removida da biblioteca.");
        } catch (error) {
            toast.error(error.message || "Erro ao remover sessão.");
        }
    };

    return (
        <div className="running-session-library">
            <div className="mb-2">
                <NeoInput
                    type="text"
                    placeholder="Buscar sessão, objetivo ou tag..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="font-size-13 mb-2"
                    size="sm"
                />
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                        <h6 className="mb-0 fw-bold font-size-13 text-dark">Biblioteca de Sessões</h6>
                        <span className="text-muted font-size-11">Modelos de corrida do sistema e da assessoria</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <NeoButton
                            type="button"
                            size="sm"
                            variant="soft-secondary"
                            className="d-flex align-items-center gap-1 fw-semibold border-0"
                            onClick={handleCreate}
                        >
                            <i className="mdi mdi-plus" />
                            Novo
                        </NeoButton>
                        <NeoInput
                            type="select"
                            size="sm"
                            value={groupFilter}
                            onChange={(event) => setGroupFilter(event.target.value)}
                            className="font-size-12"
                        >
                            <option value="all">Todos</option>
                            {RUNNING_SESSION_GROUPS.map(group => (
                                <option key={group.value} value={group.value}>{group.label}</option>
                            ))}
                        </NeoInput>
                        {loading && <i className="mdi mdi-loading mdi-spin text-primary ms-2" />}
                    </div>
                </div>
            </div>

            <div className="swim-set-library-grid">
                {visibleItems.map(item => {
                    const group = RUNNING_SESSION_GROUPS.find(groupItem => groupItem.value === item.group)?.label || item.group;
                    return (
                        <button
                            key={`${item.source}-${item.id}`}
                            type="button"
                            className="running-session-item"
                            onClick={() => onAddSession(item)}
                        >
                            <span className="fw-bold text-dark font-size-13">{item.name}</span>
                            <span className="text-muted font-size-11">
                                {group} • {item.objective || "sem objetivo"} • {item.estimatedDistanceKm || "-"} km • {item.source === "tenant" ? "Assessoria" : "Sistema"}
                            </span>
                            {item.coachNotes && <span className="text-muted font-size-11">{item.coachNotes}</span>}
                            {item.source === "tenant" && (
                                <span className="running-session-actions">
                                    <button type="button" className="btn btn-sm btn-light border-0 text-secondary" title="Editar sessão" onClick={(event) => handleEdit(event, item)}>
                                        <i className="mdi mdi-pencil-outline" />
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 text-danger" title="Excluir sessão" onClick={(event) => handleDelete(event, item)}>
                                        <i className="mdi mdi-trash-can-outline" />
                                    </button>
                                </span>
                            )}
                        </button>
                    );
                })}
                {visibleItems.length === 0 && (
                    <div className="text-center py-4 bg-light rounded border border-dashed text-muted font-size-12">
                        Nenhuma sessão encontrada.
                    </div>
                )}
            </div>
        </div>
    );
};

export default RunningSessionLibraryPicker;
